// useChatMessages.js - UPDATED VERSION (removed auto-scroll)
import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { socket } from "../../../common/socket";
import {
  addNewMessage,
  updateMessages,
  updateChatListOrder,
  clearUnread,
  clearPendingForChat
} from "../../../slices/chat/reducer";

export const useChatMessages = (
  selectedChatId,
  currentChat,
  currentUserId,
  resolveMemberById,
  isAtBottomRef
) => {
  const dispatch = useDispatch();
  const currentMessagesRef = useRef([]);

  const normalizeMessages = useCallback((messages) => {
    if (!Array.isArray(messages)) return [];
    return messages.map(msg => ({
      ...msg,
      isEdited: Boolean(msg.isEdited),
      editedAt: msg.editedAt || null,
      canEdit: msg.canEdit !== undefined ? msg.canEdit : true
    }));
  }, []);

  const handleNewMessage = useCallback((message) => {
    const chatId = message.chatId || message.chatRequestId || selectedChatId || currentChat?._id || currentChat?.chatRequestId;
    if (!chatId) {
      console.log("❌ No chat ID found for message");
      return false;
    }

    console.log("🔄 Processing new message for chat:", chatId);

    const normalizedId = message._id || message.messageId || message.id || `srv-${Date.now()}`;
    const senderId = message.sender?._id || message.senderId || message.userId;

    let resolvedSender = null;
    if (message.sender && (message.sender.firstname || message.sender.profileimg)) {
      resolvedSender = message.sender;
    } else {
      resolvedSender = resolveMemberById(senderId) || {
        _id: senderId || "unknown",
        firstname: message.senderName || "User",
        lastname: message.senderLastName || "",
        profileimg: message.senderImg || "",
      };
    }

    const normalized = {
      ...message,
      _id: normalizedId,
      sender: resolvedSender,
      isEdited: Boolean(message.isEdited),
      editedAt: message.editedAt || null,
      canEdit: message.canEdit !== undefined ? message.canEdit : true,
      createdAt: message.createdAt || new Date().toISOString(),
      status: "sent"
    };

    // ✅ FIXED: Check if this is our own pending message (optimistic update)
    const isMyMessage = String(senderId) === String(currentUserId);

    if (isMyMessage) {
      const current = currentMessagesRef.current || [];
      const pendingIdx = current.findIndex(m =>
        (m._tmp || m.status === "pending") &&
        String(m.sender?._id) === String(currentUserId) &&
        (m.content === normalized.content || m._id === normalized._tmpId)
      );

      if (pendingIdx > -1) {
        console.log("✅ Replacing pending message with server message");
        const updated = [...current];
        updated[pendingIdx] = { ...normalized, status: "sent" };
        dispatch(updateMessages({ messages: updated }));
        currentMessagesRef.current = updated;
        return false;
      }
    }

    // ✅ FIXED: Check for duplicates more thoroughly
    const exists = (currentMessagesRef.current || []).some(m =>
      String(m._id) === String(normalized._id) ||
      (m._tmp && String(m.sender?._id) === String(normalized.sender?._id) && m.content === normalized.content)
    );

    if (exists) {
      console.log("⚠️ Duplicate message detected, skipping");
      return false;
    }

    const isForCurrentChat = String(chatId) === String(selectedChatId);

    if (isForCurrentChat) {
      console.log("➕ Adding message to current chat");

      // ✅ FIXED: Add new message to the beginning since we're reversing for display
      const updatedMessages = [normalized, ...(currentMessagesRef.current || [])];

      dispatch(updateMessages({ messages: updatedMessages }));
      currentMessagesRef.current = updatedMessages;

      // ... rest of your unread count logic remains the same
      const isAtBottom = isAtBottomRef?.current !== false;

      if (isAtBottom) {
        console.log("📖 User at bottom, marking as read");
        socket.emit("markMessagesAsRead", { chatId });
        try {
          const authUser = JSON.parse(localStorage.getItem("authUser"));
          const myId = authUser?.user?._id || authUser?._id;
          if (myId) {
            dispatch(clearUnread(chatId));
            dispatch(clearPendingForChat({ chatId, userId: myId }));
          }
        } catch (error) {
          console.error("Error clearing unread:", error);
        }
      }

      return false;
    } else {
      // Different chat - backend handles unread counts; just update ordering
      console.log("💬 Message for different chat, updating list order only");
      dispatch(updateChatListOrder(chatId));
      return false;
    }
  }, [selectedChatId, currentChat, currentUserId, resolveMemberById, dispatch, isAtBottomRef]);

  const handleMessageEdited = useCallback((data) => {
    const { chatId, messageId, content, isEdited, editedAt } = data;
    const currentChatId = selectedChatId || currentChat?._id || currentChat?.chatRequestId;
    if (String(chatId) !== String(currentChatId)) return;

    if (currentChat?.messages) {
      const updatedMessages = currentChat.messages.map(msg => {
        if (String(msg._id) === String(messageId)) {
          return {
            ...msg,
            content: content,
            isEdited: true, // Force true as requested
            editedAt: editedAt || new Date().toISOString(),
            canEdit: true
          };
        }
        return msg;
      });

      dispatch(updateMessages({ messages: updatedMessages }));
      currentMessagesRef.current = updatedMessages;
    }
  }, [selectedChatId, currentChat, dispatch]);

  const handleMessagesDeleted = useCallback((data) => {
    const { chatId, messageIds = [], deletedMessageIds = [], deleteFor, updatedMessages, messageUpdates } = data;
    const currentChatId = selectedChatId || currentChat?._id || currentChat?.chatRequestId;
    if (String(chatId) !== String(currentChatId)) return;

    const idsToDelete = messageIds.length > 0 ? messageIds : deletedMessageIds;
    const serverUpdates = Array.isArray(updatedMessages) ? updatedMessages : (Array.isArray(messageUpdates) ? messageUpdates : null);

    if (currentChat?.messages) {
      let newMessages = currentChat.messages.map(msg => {
        const isTarget = idsToDelete.includes(String(msg._id));
        if (!isTarget) return msg;

        // Use server updates if available
        if (serverUpdates) {
          const fromServer = serverUpdates.find(u => String(u._id || u.messageId) === String(msg._id));
          if (fromServer) {
            return { ...msg, ...fromServer };
          }
        }

        // Handle delete for everyone - message stays visible but marked as deleted
        if (deleteFor === 'everyone') {
          return {
            ...msg,
            isDeleted: true,
            isDeleteEvery: true,
            isDeleteMe: false,
            content: "This message has been deleted",
            mediaUrl: null,
            fileName: null,
            messageType: 'text'
          };
        }

        // Handle delete for me - remove from view
        if (deleteFor === 'me') {
          return null;
        }

        return null;
      }).filter(Boolean);

      dispatch(updateMessages({ messages: newMessages }));
      currentMessagesRef.current = newMessages;
    }
  }, [selectedChatId, currentChat, dispatch]);

  return {
    normalizeMessages,
    handleNewMessage,
    handleMessageEdited,
    handleMessagesDeleted,
    currentMessagesRef
  };
};